/**
 * T19 — declarable suggestion heuristic, split out of `spatialColumnCategorization.ts` to keep
 * both files ≤200 lines. `categorizeSpatialColumn`'s own `spatial-declarable` arm covers the
 * single-column, zero-I/O name heuristic (a TEXT column named like `geom_wkt`); THIS module adds
 * the two heuristics that need MULTI-column / row-sample context a per-column function can't see:
 *
 *   1. Name arm (zero I/O) — `suggestSpatialPairs` / `findSpatialPairPartner`: lat/lng column
 *      PAIRS matched by shared name prefix within the same table.
 *   2. Value-sampling arm (zero EXTRA I/O) — `sampleDeclarableTextColumns`: TEXT columns whose
 *      already-loaded sample rows look like WKT / GeoJSON text.
 *
 * Re-exported from `spatialColumnCategorization.ts` so callers use one import path (the reverse
 * import here is TYPE-ONLY, so there is no runtime module cycle between the two files). Bundle
 * isolation: no decode-chunk imports — only the categorization module's `SpatialColumnInput` type.
 */

import type { SpatialColumnInput } from './spatialColumnCategorization';

/**
 * TEXT-typed base — same rule as (and duplicated from) `spatialColumnCategorization.isTextTypedColumn`
 * to keep this module's only cross-file dependency type-only (see the header comment above).
 */
function isTextTypedColumn(dataType: string): boolean {
  const t = (typeof dataType === 'string' ? dataType : '').trim().toLowerCase();
  return (
    t.includes('char') ||
    t.includes('text') ||
    t.includes('varchar') ||
    t.includes('nvarchar') ||
    t.includes('nchar') ||
    t.includes('ntext')
  );
}

const LAT_EXACT_NAMES: ReadonlySet<string> = new Set(['lat', 'latitude']);
const LNG_EXACT_NAMES: ReadonlySet<string> = new Set(['lng', 'lon', 'long', 'longitude']);
const LAT_SUFFIX_RE = /^(.*)_lat$/u;
const LNG_SUFFIX_RE = /^(.*)_(?:lng|lon|long)$/u;

/**
 * The shared prefix a lat-shaped name implies (`''` for a bare `lat`/`latitude`,
 * `pickup` for `pickup_lat`), or `null` when `name` isn't lat-shaped. Mirrors
 * spec §3.4's `/^(lat|latitude|.*_lat)$/i`, decomposed so the `.*_lat` capture
 * doubles as the prefix used to find the matching lng sibling. `latch` does NOT
 * match — it has no `_lat` suffix and isn't the bare word.
 */
function latPrefix(name: string): string | null {
  const lower = name.toLowerCase();
  if (LAT_EXACT_NAMES.has(lower)) return '';
  const m = LAT_SUFFIX_RE.exec(lower);
  return m ? m[1] : null;
}

/** Longitude counterpart of `latPrefix` — mirrors `/^(lng|lon|long|longitude|.*_(lng|lon|long))$/i`. */
function lngPrefix(name: string): string | null {
  const lower = name.toLowerCase();
  if (LNG_EXACT_NAMES.has(lower)) return '';
  const m = LNG_SUFFIX_RE.exec(lower);
  return m ? m[1] : null;
}

/**
 * Name arm (zero I/O, spec §3.4-1): finds lat/lng column pairs by name within
 * each `(engine, schema, table)` group and returns the set of column `name`s
 * that participate in a matched pair (both members). A bare `lat`+`lng` pair
 * matches (shared prefix `''`); `pickup_lat`+`pickup_lng` matches (shared
 * prefix `pickup`); `latch`/`longevity` never match anything (not lat/lng-
 * shaped at all). Exclusion of columns that are already native-spatial or
 * already declared is the CALLER's job (mirrors `sniffSpatialBlobColumns` /
 * the grids' existing declarable-set filtering) — this function is pure name
 * matching over whatever candidate columns it's given.
 */
export function suggestSpatialPairs(columns: SpatialColumnInput[]): Set<string> {
  const groups = new Map<string, SpatialColumnInput[]>();
  for (const column of columns) {
    const key = `${column.engine}|${column.schema}|${column.table}`;
    const group = groups.get(key);
    if (group) group.push(column);
    else groups.set(key, [column]);
  }

  const result = new Set<string>();
  for (const group of groups.values()) {
    const latsByPrefix = new Map<string, string>();
    const lngsByPrefix = new Map<string, string>();
    for (const column of group) {
      const lat = latPrefix(column.name);
      if (lat !== null && !latsByPrefix.has(lat)) latsByPrefix.set(lat, column.name);
      const lng = lngPrefix(column.name);
      if (lng !== null && !lngsByPrefix.has(lng)) lngsByPrefix.set(lng, column.name);
    }
    for (const [prefix, latName] of latsByPrefix) {
      const lngName = lngsByPrefix.get(prefix);
      if (lngName) {
        result.add(latName);
        result.add(lngName);
      }
    }
  }
  return result;
}

/**
 * Best-guess LNG partner for `columnName` among `siblingNames` in the same
 * table, using the same prefix matching as `suggestSpatialPairs`. Returns
 * `null` when `columnName` isn't lat-shaped or no sibling matches its prefix.
 * Used to pre-select the "Longitude column" dropdown default when a user
 * declares a name-arm-suggested pair via the `⌖?` glyph.
 */
export function findSpatialPairPartner(columnName: string, siblingNames: string[]): string | null {
  const prefix = latPrefix(columnName);
  if (prefix === null) return null;
  for (const sibling of siblingNames) {
    if (lngPrefix(sibling) === prefix) return sibling;
  }
  return null;
}

// WKT-looking text (spec §3.4-2): an optional `SRID=n;` prefix followed by one
// of the 7 WKT geometry keywords and an opening paren.
const WKT_LOOKING_RE =
  /^\s*(?:SRID=\d+;)?\s*(?:POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION)\s*\(/iu;

// GeoJSON geometry `type` values (spec §3.4-2) — mirrors decodeDeclaredSpatialValue's
// GEOJSON_GEOMETRY_TYPES, duplicated (not imported) per this file's bundle-isolation
// contract: it must never reach into the lazy wkx-decode chunk.
const GEOJSON_GEOMETRY_TYPE_NAMES: ReadonlySet<string> = new Set([
  'Point',
  'LineString',
  'Polygon',
  'MultiPoint',
  'MultiLineString',
  'MultiPolygon',
  'GeometryCollection',
]);

function looksLikeGeoJsonGeometry(value: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== 'object') return false;
  const type = (parsed as { type?: unknown }).type;
  return typeof type === 'string' && GEOJSON_GEOMETRY_TYPE_NAMES.has(type);
}

function looksSpatialText(value: string): boolean {
  return WKT_LOOKING_RE.test(value) || looksLikeGeoJsonGeometry(value);
}

const MAX_TEXT_SAMPLE_ROWS = 20;
const TEXT_SAMPLE_DECLARABLE_RATIO = 0.8;

/**
 * Value-sampling arm (zero EXTRA I/O, spec §3.4-2 — REQUIRED, not optional).
 * For each TEXT-typed column (a numeric/binary column is never sampled — the
 * `isTextTypedColumn` gate below), inspects up to `MAX_TEXT_SAMPLE_ROWS`
 * already-in-memory `sampleRows` and flags the column declarable when at least
 * 80% of its NON-NULL sampled values look like WKT or a GeoJSON geometry.
 * Deterministic for a given loaded window; NEVER queries the database — runs
 * inside the grids' existing categorization `useMemo` (same seam as T4's
 * `sniffSpatialBlobColumns`). Returns the set of promoted column `name`s.
 */
export function sampleDeclarableTextColumns(
  columns: SpatialColumnInput[],
  sampleRows: Record<string, unknown>[],
): Set<string> {
  const result = new Set<string>();
  for (const column of columns) {
    if (!isTextTypedColumn(column.dataType)) continue;

    let total = 0;
    let hits = 0;
    const limit = Math.min(sampleRows.length, MAX_TEXT_SAMPLE_ROWS);
    for (let i = 0; i < limit; i += 1) {
      const value = sampleRows[i]?.[column.name];
      if (value === null || value === undefined) continue;
      total += 1;
      if (typeof value === 'string' && looksSpatialText(value)) hits += 1;
    }
    if (total === 0) continue;
    if (hits / total >= TEXT_SAMPLE_DECLARABLE_RATIO) result.add(column.name);
  }
  return result;
}
